<!--
  Passwort über einen Einladungslink vergeben.

  Wir verschicken nie ein Passwort, nur einen einmaligen Link. Deshalb ist
  diese Seite der einzige Weg, wie ein Kunde zu seinem ersten Passwort kommt.
-->
<script lang="ts">
  import { api } from '../lib/api.ts';
  import { MIN_PASSWORTLAENGE, portalBeweis } from '../lib/zugang.ts';

  let { token }: { token: string } = $props();

  let passwort = $state('');
  let wiederholung = $state('');
  let fehler = $state<string | null>(null);
  let fertig = $state(false);
  let laeuft = $state(false);

  /**
   * Die Adresse gehört zum Passwort: Aus ihr entsteht das Salz der
   * Schlüsselableitung. Sie steht nicht im Link, deshalb wird sie hier geholt.
   */
  let email = $state<string | null>(null);
  const bereit = $derived(email !== null);

  // Der Wächter ist nötig: Ohne ihn läuft der Effekt zweimal und fragt die
  // Adresse doppelt ab. Harmlos, aber es zählt gegen das Anfragelimit und
  // steht ohne Grund im Serverprotokoll.
  let geholt = false;
  $effect(() => {
    if (geholt) return;
    geholt = true;
    void (async () => {
      try {
        const antwort = await api.get<{ email: string }>(
          `/api/portal/einladung/${encodeURIComponent(token)}`,
        );
        email = antwort.email;
      } catch (f) {
        fehler = f instanceof Error ? f.message : 'Diese Einladung ist nicht mehr gültig';
      }
    })();
  });

  const zuKurz = $derived(passwort.length > 0 && passwort.length < MIN_PASSWORTLAENGE);
  const ungleich = $derived(wiederholung.length > 0 && passwort !== wiederholung);

  async function setzen(ereignis: SubmitEvent): Promise<void> {
    ereignis.preventDefault();
    if (email === null) return;
    laeuft = true;
    fehler = null;
    try {
      // Das Passwort verlässt diesen Browser nicht — nur der Beweis geht raus.
      const beweis = await portalBeweis(email, passwort);
      await api.post('/api/portal/einladung', { token, beweis });
      fertig = true;
    } catch (f) {
      fehler = f instanceof Error ? f.message : 'Das hat nicht geklappt';
    } finally {
      laeuft = false;
    }
  }
</script>

<div class="anmeldeseite">
  <h1>Willkommen</h1>

  <div class="karte">
    {#if fertig}
      <p class="erfolg">Ihr Passwort ist gesetzt.</p>
      <p>Sie können sich jetzt anmelden.</p>
      <div class="knopfreihe">
        <button type="button" onclick={() => (location.hash = '/')}>Zur Anmeldung</button>
      </div>
    {:else}
      <p>Vergeben Sie hier Ihr Passwort für den Kundenbereich.</p>
      <p class="wichtig">
        Ein <strong>ganzer Satz</strong> ist ein gutes Passwort: leicht zu merken und trotzdem
        sicher. Zum Beispiel drei Wörter, die nur für Sie zusammengehören.
      </p>

      {#if fehler}<p class="fehler">{fehler}</p>{/if}

      <form onsubmit={setzen}>
        <label for="p1">Ihr neues Passwort</label>
        <input id="p1" type="password" autocomplete="new-password" bind:value={passwort} required />
        {#if zuKurz}<p class="leise klein">Noch etwas länger, bitte — mindestens zehn Zeichen.</p>{/if}

        <label for="p2">Noch einmal zur Sicherheit</label>
        <input id="p2" type="password" autocomplete="new-password" bind:value={wiederholung} required />
        {#if ungleich}<p class="leise klein">Die beiden stimmen noch nicht überein.</p>{/if}

        <div class="knopfreihe">
          <button type="submit" disabled={laeuft || !bereit || zuKurz || ungleich || !wiederholung}>
            {laeuft ? 'Einen Moment …' : 'Passwort speichern'}
          </button>
        </div>
        {#if laeuft}
          <p class="leise klein">
            Ihr Passwort wird auf diesem Gerät verrechnet — das dauert einen Augenblick und
            passiert genau deshalb hier und nicht bei uns.
          </p>
        {/if}
      </form>
    {/if}
  </div>
</div>
